import { QueryClient } from "@tanstack/react-query";

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: 1,
    },
  },
});

// ── Query key factories ──

// Filter + sort state that parameterizes a paginated list query key. Mirrors
// `DataGridQuery` from `@/hooks/use-data-grid`; kept structural to avoid a cycle.
interface ListKeyFilters {
  search?: string;
  status?: string;
  sort?: string;
  sortDir?: string;
}

// Reference entity (src/examples/widget). One factory per domain; mirror this
// shape for a real entity and invalidate via `*.all` after mutations.
export const widgetKeys = {
  all: ["widgets"] as const,
  list: (filters: ListKeyFilters) => ["widgets", "list", filters] as const,
  detail: (id: string) => ["widgets", "detail", id] as const,
};

export const allowancePeriodKeys = {
  all: ["allowance-period"] as const,
  detail: () => ["allowance-period", "detail"] as const,
} as const;

export const recommendationSourceKeys = {
  all: ["recommendation-source"] as const,
  detail: () => ["recommendation-source", "detail"] as const,
} as const;

export const ptoBalanceKeys = {
  all: ["pto-balance"] as const,
  detail: () => ["pto-balance", "detail"] as const,
} as const;

export const ptoEntryKeys = {
  all: ["pto-entries"] as const,
  list: (filters: ListKeyFilters) => ["pto-entries", "list", filters] as const,
  detail: (id: string) => ["pto-entries", "detail", id] as const,
} as const;

export const googleContactKeys = {
  search: (term: string) => ["google-contacts", "search", term] as const,
} as const;

export const ptoEntryCalendarEventKeys = {
  lastSent: (entryId: string) => ["pto-entries", "calendar-event", "last-sent", entryId] as const,
} as const;

export const googleConnectionKeys = {
  status: ["google-connection", "status"] as const,
} as const;

export const googleAvailabilityKeys = {
  connected: ["google-availability", "connected"] as const,
} as const;

export const activitySuggestionKeys = {
  all: ["activity-suggestions"] as const,
  list: (filters: ListKeyFilters) => ["activity-suggestions", "list", filters] as const,
  detail: (id: string) => ["activity-suggestions", "detail", id] as const,
} as const;

// EPIC-009: derived, ephemeral — not a resource with its own invalidation
// lifecycle, so just the one key shape (no `.all`/`.list`/`.detail` split).
export const recommendationGenerationKeys = {
  forEntry: (entryId: string, category: string) =>
    ["recommendation-generation", entryId, category] as const,
} as const;

export const fileKeys = {
  all: ["files"] as const,
  list: (filters: ListKeyFilters) => ["files", "list", filters] as const,
  detail: (id: string) => ["files", "detail", id] as const,
} as const;

export const devKeys = {
  seedAccounts: ["dev", "seed-accounts"] as const,
} as const;

export const auditKeys = {
  all: ["audit"] as const,
  list: (filters: ListKeyFilters) => ["audit", "list", filters] as const,
} as const;
